import { Center, Flex, Image, Text, VStack, Box, Progress, Spinner, HStack, CircularProgress } from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import InlineSVG from "react-inlinesvg";
import { BoardBox } from "../Display/BoardBox";
import { ClueBox } from "../Display/Clue";
import { PlayerBox } from "../Display/PlayerBox";
import { TextFit } from "../TextFit";
import { AnimatePresence, motion } from "framer-motion";
import { JeopardyStates, Rounds, Player } from "types";
import { useLobbyMusic } from "../../hooks/useLobbyMusic";
import { useViewportScale } from "../../hooks/useViewportScale";
import { useGameSounds } from "../../hooks/useGameSounds";
import { useElevenLabsTTS } from "../../hooks/useElevenLabsTTS";

// Player carousel component for the waiting screen
interface PlayerCarouselProps {
  players: Player[];
  playerAudioProgress?: Record<string, { status: string; current: number; total: number }>;
}

const PlayerCarousel: React.FC<PlayerCarouselProps> = ({ players, playerAudioProgress }) => {
  const shouldAnimate = players.length > 10;
  const cardWidthVw = 20;
  const gapVw = 2;
  const cardSlotVw = cardWidthVw + gapVw; // 22vw per card slot

  // Split players: first 5 top, next 5 bottom (for static display)
  const topPlayers = players.slice(0, 5);
  const bottomPlayers = players.slice(5, 10);

  // Render a single player card
  const renderPlayerCard = (player: Player, index: number) => {
    const audioProgress = playerAudioProgress?.[player.name];
    const isGenerating = audioProgress?.status === "generating";
    const isComplete = audioProgress?.status === "complete";

    return (
      <VStack
        key={`${player.name}-${index}`}
        bg="rgba(255,255,255,0.15)"
        width={`${cardWidthVw}vw`}
        height="14vw"
        justifyContent="center"
        borderRadius="xl"
        spacing="0.5vh"
        backdropFilter="blur(4px)"
        alignItems="center"
        flexShrink={0}
      >
        {player.nameSvg && (
          <Box
            width="16vw"
            height="10vw"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <InlineSVG
              src={player.nameSvg}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        )}
        <HStack spacing="0.5vh">
          <Text textStyle="clue" fontSize="2vh">
            {player.name}
          </Text>
          {isGenerating && (
            <CircularProgress
              value={(audioProgress.current / audioProgress.total) * 100}
              size="2vh"
              color="yellow.400"
              trackColor="whiteAlpha.300"
            />
          )}
          {isComplete && (
            <Text fontSize="2vh" color="green.400">
              ✓
            </Text>
          )}
        </HStack>
      </VStack>
    );
  };

  // Static display for ≤10 players
  if (!shouldAnimate) {
    return (
      <>
        {topPlayers.length > 0 && (
          <Box
            position="absolute"
            top="1.5vh"
            left={0}
            right={0}
            height="17vw"
            pointerEvents="none"
            zIndex={5}
          >
            <Flex gap={`${gapVw}vw`} height="100%" justifyContent="center">
              {topPlayers.map((player, i) => renderPlayerCard(player, i))}
            </Flex>
          </Box>
        )}

        {bottomPlayers.length > 0 && (
          <Box
            position="absolute"
            bottom="1.5vh"
            left={0}
            right={0}
            height="17vw"
            pointerEvents="none"
            zIndex={5}
          >
            <Flex gap={`${gapVw}vw`} height="100%" justifyContent="center" alignItems="flex-end">
              {bottomPlayers.map((player, i) => renderPlayerCard(player, i))}
            </Flex>
          </Box>
        )}
      </>
    );
  }

  // Conveyor belt animation for >10 players
  // Each card travels: top row (right→left) → bottom row (left→right) → wait → repeat
  //
  // Two separate clipped rows - cards only move horizontally within each
  // Each card appears in both rows but is only visible during its "active" phase
  
  const secondsPerSlot = 2;
  
  // Full circuit time must be long enough for both rows + any waiting
  // Minimum is 2 rows worth (10 visible slots), scale up for more players
  const minCircuitSlots = Math.max(players.length, 12);
  const fullCircuitTime = minCircuitSlots * secondsPerSlot;
  
  // Each row takes ~45% of the cycle, with 5% gaps for transitions
  // 0-45%: top row | 45-50%: gap | 50-95%: bottom row | 95-100%: gap
  
  // Get animation delay for each card (staggered entry)
  const getCardDelay = (index: number) => -(index * secondsPerSlot);

  // Render card for animation
  const renderAnimatedCard = (player: Player, index: number, row: "top" | "bottom") => {
    const audioProgress = playerAudioProgress?.[player.name];
    const isGenerating = audioProgress?.status === "generating";
    const isComplete = audioProgress?.status === "complete";
    
    return (
      <Box
        key={`${row}-${player.name}`}
        position="absolute"
        top={0}
        left={0}
        sx={{
          animation: `conveyor${row === "top" ? "Top" : "Bottom"} ${fullCircuitTime}s linear infinite`,
          animationDelay: `${getCardDelay(index)}s`,
        }}
      >
        <VStack
          bg="rgba(255,255,255,0.15)"
          width={`${cardWidthVw}vw`}
          height="14vw"
          justifyContent="center"
          borderRadius="xl"
          spacing="0.5vh"
          backdropFilter="blur(4px)"
          alignItems="center"
        >
          {player.nameSvg && (
            <Box
              width="16vw"
              height="10vw"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <InlineSVG
                src={player.nameSvg}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
          <HStack spacing="0.5vh">
            <Text textStyle="clue" fontSize="2vh">
              {player.name}
            </Text>
            {isGenerating && (
              <CircularProgress
                value={(audioProgress.current / audioProgress.total) * 100}
                size="2vh"
                color="yellow.400"
                trackColor="whiteAlpha.300"
              />
            )}
            {isComplete && (
              <Text fontSize="2vh" color="green.400">
                ✓
              </Text>
            )}
          </HStack>
        </VStack>
      </Box>
    );
  };

  return (
    <>
      {/* Top row - cards scroll right to left, visible during 0-45% of cycle */}
      <Box
        position="absolute"
        top="1.5vh"
        left={0}
        right={0}
        height="16vw"
        overflow="hidden"
        pointerEvents="none"
        zIndex={5}
        sx={{
          "@keyframes conveyorTop": {
            // Start off-screen right
            "0%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
            // Cross to off-screen left
            "45%": { transform: `translateX(-${cardWidthVw * 2}vw)` },
            // Jump back to off-screen right (instant, same position visually hidden)
            "45.1%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
            // Stay off-screen right while on bottom row or waiting
            "100%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
          },
        }}
      >
        <Box position="relative" height="100%">
          {players.map((player, i) => renderAnimatedCard(player, i, "top"))}
        </Box>
      </Box>

      {/* Bottom row - cards scroll left to right, visible during 50-95% of cycle */}
      <Box
        position="absolute"
        bottom="1.5vh"
        left={0}
        right={0}
        height="16vw"
        overflow="hidden"
        pointerEvents="none"
        zIndex={5}
        sx={{
          "@keyframes conveyorBottom": {
            // Start off-screen right (waiting position)
            "0%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
            // Stay off-screen while on top row
            "49.9%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
            // Jump to off-screen left to start bottom crossing
            "50%": { transform: `translateX(-${cardWidthVw * 2}vw)` },
            // Cross to off-screen right
            "95%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
            // Stay off-screen right while waiting
            "100%": { transform: `translateX(calc(100vw + ${cardWidthVw}vw))` },
          },
        }}
      >
        <Box position="relative" height="100%" display="flex" alignItems="flex-end">
          {players.map((player, i) => renderAnimatedCard(player, i, "bottom"))}
        </Box>
      </Box>
    </>
  );
};

const qrCodeBaseUrl =
  "https://api.qrserver.com/v1/create-qr-code/?size=150x150";
const clueArray = Array.from({ length: 30 }, (_, i) => (i % 6) + 1);
const clueRenderOrder = clueArray.sort(() => Math.random() - 0.5);

// Custom message namespace - must match the sender
const CAST_NAMESPACE = "urn:x-cast:com.jeoparty.game";

// Cast Receiver options interface
interface CastReceiverOptions {
  maxInactivity?: number; // Max idle time in seconds before disconnect (default 600)
  disableIdleTimeout?: boolean;
}

// Cast Receiver context interface (loaded dynamically on Chromecast)
interface CastReceiverContext {
  start(options?: CastReceiverOptions): void;
  stop(): void;
  setInactivityTimeout(seconds: number): void;
  addCustomMessageListener(
    namespace: string,
    listener: (event: { data: unknown }) => void
  ): void;
  sendCustomMessage(
    namespace: string,
    senderId: string | undefined,
    message: unknown
  ): void;
}

// Helper to get the receiver context (only available on Chromecast)
const getCastReceiverContext = (): CastReceiverContext | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const castReceiver = w.cast?.framework?.CastReceiverContext;
    if (castReceiver) {
      return castReceiver.getInstance();
    }
    return null;
  } catch {
    return null;
  }
};

export const CastReceiver: React.FC = () => {
  const { gameId: urlGameId } = useParams<{ gameId: string }>();
  const [searchParams] = useSearchParams();
  const {
    gameId: contextGameId,
    gameState,
    isLoadingGameState,
    setGameId,
    ping,
  } = useGame();

  // Debug state - stores messages from cast receiver
  const [castMessages, setCastMessages] = useState<string[]>([]);

  // Calculate viewport scale to prevent overflow on smaller screens
  // Target size: 1920x1080 (common TV/display resolution)
  const viewportScale = useViewportScale(1920, 1080, 0.1, 1.0);

  // Play theme music while waiting for players or during final jeopardy guessing
  const isWaitingForPlayers = gameState?.currentState === JeopardyStates.WaitingForPlayers;
  const isFinalJeopardyGuessing = gameState?.currentState === JeopardyStates.MakeFinalJeopardyGuesses;
  const { startMusic: startThemeMusic } = useLobbyMusic(isWaitingForPlayers || isFinalJeopardyGuessing);

  // Play board fill sound when board first appears
  const { playBoardFill } = useGameSounds();
  const hasPlayedBoardFill = useRef(false);

  // TTS for category announcements and voice feedback
  const {
    playCategoryAnnouncement,
    playPersonalizedCorrect,
    playPersonalizedIncorrect,
    playBoardControl,
    playAnswerReveal,
    unlockAudio,
    isAudioUnlocked,
  } = useElevenLabsTTS();
  const announcedRoundsRef = useRef<Set<string>>(new Set());
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const audioUnlockAttemptedRef = useRef(false);

  // State tracking for voice feedback
  const previousStateRef = useRef<JeopardyStates | undefined>();
  const buzzedInPlayerScoreRef = useRef<{ name: string; score: number } | null>(null);
  const previousClueAnswerAudioRef = useRef<string | undefined>();

  // Check if running on actual Chromecast (vs browser testing)
  const isActualChromecast = !!getCastReceiverContext();

  // Try to unlock audio when game state first loads
  useEffect(() => {
    if (gameState && !audioUnlockAttemptedRef.current) {
      audioUnlockAttemptedRef.current = true;
      unlockAudio().then((success) => {
        if (!success && !isActualChromecast) {
          // Only show prompt in browser testing, not on real Chromecast
          console.log('[CastReceiver] Audio unlock failed - showing prompt (browser testing)');
          setShowAudioPrompt(true);
        }
      });
    }
  }, [gameState, unlockAudio, isActualChromecast]);

  // Handle tap to enable sound (browser testing only)
  const handleEnableSound = useCallback(async () => {
    const success = await unlockAudio();
    if (success) {
      setShowAudioPrompt(false);
    }
  }, [unlockAudio]);

  // Hide prompt once audio is unlocked
  useEffect(() => {
    if (isAudioUnlocked) {
      setShowAudioPrompt(false);
    }
  }, [isAudioUnlocked]);

  // Start theme music when audio is unlocked and we're in waiting/final jeopardy state
  useEffect(() => {
    if (isAudioUnlocked && (isWaitingForPlayers || isFinalJeopardyGuessing)) {
      startThemeMusic();
    }
  }, [isAudioUnlocked, isWaitingForPlayers, isFinalJeopardyGuessing, startThemeMusic]);

  // Store setCastMessages in window so initializeReceiver can access it
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__addCastDebug = (msg: string) => {
      console.log("[CastReceiver]", msg);
      setCastMessages((prev) => [...prev.slice(-4), msg]);
    };
  }, []);

  // Use gameId from URL if available, otherwise from context (set via cast message)
  const effectiveGameId = urlGameId || contextGameId;

  // Set game ID from URL or from cast message
  useEffect(() => {
    // Check for gameId in URL params (for direct access)
    const gameIdFromQuery = searchParams.get("gameId");
    const finalGameId = urlGameId || gameIdFromQuery;

    if (finalGameId) {
      setGameId(finalGameId);
    }
  }, [urlGameId, searchParams, setGameId]);

  // Initialize Cast Receiver SDK if running on Chromecast
  useEffect(() => {
    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src*="cast_receiver_framework"]'
    );
    if (existingScript) {
      initializeReceiver();
      return;
    }

    // Load Cast Receiver SDK
    const script = document.createElement("script");
    script.src =
      "//www.gstatic.com/cast/sdk/libs/caf_receiver/v3/cast_receiver_framework.js";
    script.async = true;

    script.onload = () => {
      // Small delay to ensure framework is ready
      setTimeout(() => {
        initializeReceiver();
      }, 100);
    };

    document.head.appendChild(script);

    function initializeReceiver() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const debug = (window as any).__addCastDebug || console.log;
      const context = getCastReceiverContext();

      debug(`Cast context: ${context ? "found" : "not found"}`);

      if (context) {
        // Listen for custom messages from sender
        try {
          context.addCustomMessageListener(CAST_NAMESPACE, (event) => {
            const data = event.data as { type: string; gameId?: string };
            debug(`Message received: ${JSON.stringify(data)}`);
            if (data.type === "LOAD_GAME" && data.gameId) {
              debug(`Setting game ID: ${data.gameId}`);
              setGameId(data.gameId);
            } else if (data.type === "HEARTBEAT") {
              // Acknowledge heartbeat from sender
              debug("Heartbeat received from sender");
            }
          });
          debug("Message listener registered");
        } catch (e) {
          debug(`Listener error: ${e}`);
        }

        try {
          // Try to disable idle timeout completely first (if supported)
          try {
            context.start({ disableIdleTimeout: true });
            debug("Receiver started with idle timeout disabled");
          } catch {
            // If disableIdleTimeout is not supported, use extended timeout
            // Set to 8 hours (28800 seconds) - effectively unlimited for a game session
            context.start({ maxInactivity: 28800 });
            debug("Receiver started with extended timeout (8 hours)");
          }

          // Also try to set inactivity timeout directly as a fallback
          try {
            context.setInactivityTimeout(28800); // 8 hours
          } catch {
            // Method may not exist on all versions
          }

          // Send READY message to let sender know we're listening
          context.sendCustomMessage(CAST_NAMESPACE, undefined, { type: "READY" });
          debug("READY signal sent");

          // Send periodic heartbeat to prevent idle disconnect
          // Send every 30 seconds to be more aggressive about keeping connection alive
          heartbeatInterval = setInterval(() => {
            try {
              context.sendCustomMessage(CAST_NAMESPACE, undefined, { type: "HEARTBEAT" });
              debug("Heartbeat sent");
            } catch (e) {
              debug(`Heartbeat error: ${e}`);
            }
          }, 30000); // Every 30 seconds
        } catch (e) {
          debug(`Start error: ${e}`);
        }
      }
    }

    return () => {
      // Cleanup
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      const context = getCastReceiverContext();
      if (context) {
        context.stop();
      }
    };
  }, [setGameId, urlGameId]);

  // Keep connection alive
  useEffect(() => {
    const pingInterval = ping();
    return () => clearInterval(pingInterval);
  }, [ping]);

  // Use audience URL if audienceOnly mode is enabled
  const isAudienceOnly = gameState?.audienceOnly ?? false;
  const joinUrl = isAudienceOnly
    ? `${window.location.origin}/audience/${effectiveGameId}`
    : `${window.location.origin}/play/${effectiveGameId}`;
  const qrCodeUrl = `${qrCodeBaseUrl}&data=${encodeURIComponent(joinUrl)}`;

  const categories = gameState?.categories;
  const players = gameState?.players;
  const finalJeopardy =
    gameState &&
    [
      JeopardyStates.CollectFinalJeopardyWagers,
      JeopardyStates.MakeFinalJeopardyGuesses,
      JeopardyStates.RevealFinalJeopardyAnswer,
    ].includes(gameState.currentState);

  // Announce categories sequentially
  const announceCategoriesSequentially = useCallback(async () => {
    if (!categories || !gameState?.categoryAudioUrls) return;

    const currentRoundCategories = categories.filter(
      (category) => category.roundType === gameState?.currentRound
    );

    // Wait for board fill animation to complete (matches the animation delay in ClueBox)
    await new Promise(resolve => setTimeout(resolve, 4500));

    // Announce each category with a pause between
    for (const category of currentRoundCategories) {
      const audioUrl = gameState.categoryAudioUrls[category.id];
      if (audioUrl) {
        await playCategoryAnnouncement(audioUrl);
        // Small pause between announcements
        await new Promise(resolve => setTimeout(resolve, 400));
      }
    }
  }, [categories, gameState?.categoryAudioUrls, gameState?.currentRound, playCategoryAnnouncement]);

  // Play board fill sound when board first appears and all clues are unanswered
  useEffect(() => {
    if (
      categories &&
      categories.length > 0 &&
      gameState?.currentState === JeopardyStates.CategorySelection &&
      !hasPlayedBoardFill.current
    ) {
      // Check if all clues in the current round are unanswered
      const currentRoundCategories = categories.filter(
        (category) => category.roundType === gameState?.currentRound
      );
      const allCluesUnanswered = currentRoundCategories.every((category) =>
        category.clues.every((clue) => !clue.answered)
      );

      if (allCluesUnanswered) {
        playBoardFill();
        hasPlayedBoardFill.current = true;

        // Announce categories if this round hasn't been announced yet
        const roundKey = gameState?.currentRound || 'jeopardy';
        if (!announcedRoundsRef.current.has(roundKey)) {
          announcedRoundsRef.current.add(roundKey);
          announceCategoriesSequentially().catch(console.error);
        }
      }
    }
    // Reset when round changes
    if (gameState?.currentState === JeopardyStates.RoundEnd) {
      hasPlayedBoardFill.current = false;
    }
  }, [categories, gameState?.currentState, gameState?.currentRound, playBoardFill, announceCategoriesSequentially]);

  // Store active clue's answer audio before it gets cleared on state transition
  useEffect(() => {
    if (gameState?.activeClue?.answerAudioUrl) {
      previousClueAnswerAudioRef.current = gameState.activeClue.answerAudioUrl;
    }
  }, [gameState?.activeClue?.answerAudioUrl]);

  // Capture buzzed-in player's score when they buzz in (before answer is evaluated)
  useEffect(() => {
    if (gameState?.currentState === JeopardyStates.PlayerAnswering && gameState?.buzzedInPlayer) {
      buzzedInPlayerScoreRef.current = {
        name: gameState.buzzedInPlayer.name,
        score: gameState.buzzedInPlayer.score,
      };
    }
  }, [gameState?.currentState, gameState?.buzzedInPlayer]);

  // Voice TTS feedback on Cast device (correct/incorrect, board control, answer reveals)
  useEffect(() => {
    if (!gameState) return;

    const currentState = gameState.currentState;
    const previousState = previousStateRef.current;
    const activePlayer = gameState.activePlayer;
    const isDailyDouble = gameState.activeClue?.isDailyDouble;
    const playerAudio = gameState.playerAudio;
    const players = gameState.players;

    // Check if the buzzed-in player's score increased (they got it right)
    const checkIfAnswerWasCorrect = (): boolean => {
      const captured = buzzedInPlayerScoreRef.current;
      if (!captured) return false;

      // Find the player in the current players list
      const player = players?.find(p => p.name === captured.name);
      if (!player) return false;

      return player.score > captured.score;
    };

    // Wrong answer - player answered incorrectly but others can still try
    if (
      previousState === JeopardyStates.PlayerAnswering &&
      currentState === JeopardyStates.WaitingForBuzz
    ) {
      const playerName = buzzedInPlayerScoreRef.current?.name;
      const incorrectAudioUrl = playerName ? playerAudio?.[playerName]?.incorrectFeedback : undefined;
      playPersonalizedIncorrect(incorrectAudioUrl).catch(console.error);
    }

    // Correct answer or all players wrong/time expired
    if (
      previousState === JeopardyStates.PlayerAnswering &&
      currentState === JeopardyStates.CategorySelection
    ) {
      const wasCorrect = checkIfAnswerWasCorrect();
      const answeringPlayerName = buzzedInPlayerScoreRef.current?.name;
      const currentActivePlayerName = activePlayer?.name || null;

      if (wasCorrect && answeringPlayerName) {
        // Score increased - correct answer
        const correctAudioUrl = playerAudio?.[answeringPlayerName]?.correctFeedback;

        // Play correct feedback, then board control
        playPersonalizedCorrect(correctAudioUrl)
          .then(() => {
            if (currentActivePlayerName) {
              const boardControlUrl = playerAudio?.[currentActivePlayerName]?.controlsBoard;
              return playBoardControl(boardControlUrl);
            }
          })
          .catch(console.error);

      } else if (!isDailyDouble) {
        // Wrong/timeout - play answer reveal then board control
        const answerAudioUrl = previousClueAnswerAudioRef.current;

        (async () => {
          // Wait a moment for times up sound on player devices
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Play answer reveal
          if (answerAudioUrl) {
            await playAnswerReveal(answerAudioUrl);
          }

          // Play board control announcement
          if (currentActivePlayerName) {
            await new Promise(resolve => setTimeout(resolve, 500));
            const boardControlUrl = playerAudio?.[currentActivePlayerName]?.controlsBoard;
            await playBoardControl(boardControlUrl);
          }
        })().catch(console.error);
      }
    }

    // Nobody buzzed in - time ran out
    if (
      previousState === JeopardyStates.WaitingForBuzz &&
      currentState === JeopardyStates.CategorySelection
    ) {
      const currentActivePlayerName = activePlayer?.name || null;
      const answerAudioUrl = previousClueAnswerAudioRef.current;

      (async () => {
        // Wait a moment for times up sound on player devices
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Play answer reveal
        if (answerAudioUrl) {
          await playAnswerReveal(answerAudioUrl);
        }

        // Play board control announcement
        if (currentActivePlayerName) {
          await new Promise(resolve => setTimeout(resolve, 500));
          const boardControlUrl = playerAudio?.[currentActivePlayerName]?.controlsBoard;
          await playBoardControl(boardControlUrl);
        }
      })().catch(console.error);
    }

    previousStateRef.current = currentState;
  }, [
    gameState?.currentState,
    gameState?.activePlayer?.name,
    gameState?.activeClue?.isDailyDouble,
    gameState?.playerAudio,
    gameState?.players,
    playPersonalizedCorrect,
    playPersonalizedIncorrect,
    playBoardControl,
    playAnswerReveal,
  ]);

  const finalJeopardyCategory = categories?.find(
    (c) => c.roundType === Rounds.FinalJeopardy
  );
  const finalJeopardyClue = finalJeopardyCategory?.clues[0];

  // Waiting for game ID from sender (cast message)
  if (!effectiveGameId) {
    return (
      <Box
        width="100vw"
        height="100vh"
        overflow="hidden"
        backgroundColor="jeopardy.blue"
        position="relative"
        style={{
          transform: `scale(${viewportScale})`,
          transformOrigin: "top left",
          width: `${100 / viewportScale}vw`,
          height: `${100 / viewportScale}vh`,
        }}
      >
        <Center width="100%" height="100%" position="relative">
          <VStack spacing={8}>
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.8, 1, 0.8],
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Text fontSize="6em" textStyle="jeoparty">
                JEOPARTY
              </Text>
            </motion.div>
            <Box
              width="80px"
              height="80px"
              borderRadius="full"
              border="6px solid"
              borderColor="whiteAlpha.300"
              borderTopColor="white"
              animation="spin 1s linear infinite"
              sx={{
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </VStack>
        </Center>
      </Box>
    );
  }

  if (isLoadingGameState || !gameState) {
    // Check if we have game state with TTS progress even if still loading
    const ttsProgress = gameState?.ttsProgress;
    const showProgress = ttsProgress && ttsProgress.total > 0;

    return (
      <Box
        width="100vw"
        height="100vh"
        overflow="hidden"
        backgroundColor="jeopardy.blue"
        position="relative"
        style={{
          transform: `scale(${viewportScale})`,
          transformOrigin: "top left",
          width: `${100 / viewportScale}vw`,
          height: `${100 / viewportScale}vh`,
        }}
      >
        <Center width="100%" height="100%" position="relative">
          <VStack spacing={6} maxWidth="800px" px={8}>
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.7, 1, 0.7],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Text fontSize="6em" textStyle="jeoparty">
                JEOPARTY
              </Text>
            </motion.div>
            <VStack spacing={4} width="100%">
              {showProgress ? (
                <>
                  <Text textStyle="clue" fontSize="2xl">
                    {ttsProgress.message || "Generating game..."}
                  </Text>
                  <Progress
                    value={(ttsProgress.current / ttsProgress.total) * 100}
                    size="lg"
                    colorScheme="yellow"
                    bg="rgba(255,255,255,0.2)"
                    borderRadius="md"
                    width="100%"
                    height="30px"
                  />
                  <Text textStyle="clue" fontSize="xl" opacity={0.8}>
                    {ttsProgress.current} / {ttsProgress.total} audio files
                  </Text>
                </>
              ) : (
                <>
                  <Spinner size="xl" color="white" thickness="4px" />
                  <Text textStyle="clue" fontSize="2xl">
                    Loading game...
                  </Text>
                </>
              )}
            </VStack>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!gameState) {
    return (
      <Box
        width="100vw"
        height="100vh"
        overflow="hidden"
        backgroundColor="jeopardy.blue"
        position="relative"
        style={{
          transform: `scale(${viewportScale})`,
          transformOrigin: "top left",
          width: `${100 / viewportScale}vw`,
          height: `${100 / viewportScale}vh`,
        }}
      >
        <Center width="100%" height="100%" position="relative">
          <VStack spacing={4}>
            <Text fontSize="4em" textStyle="jeoparty">
              JEOPARTY
            </Text>
            <Text textStyle="clue" fontSize="xl">
              Game not found
            </Text>
            <Text textStyle="clue" opacity={0.7}>
              Check the game code and try again
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  // Game Over - Winner screen
  if (gameState.currentState === JeopardyStates.GameOver) {
    const sortedPlayers = [...(gameState.players || [])].sort((a, b) => b.score - a.score);
    const winner = sortedPlayers[0];
    const hasWinner = winner && winner.score > 0;

    return (
      <Box
        width="100vw"
        height="100vh"
        overflow="hidden"
        backgroundColor="jeopardy.blue"
        position="relative"
        style={{
          transform: `scale(${viewportScale})`,
          transformOrigin: "top left",
          width: `${100 / viewportScale}vw`,
          height: `${100 / viewportScale}vh`,
        }}
      >
        <Center width="100%" height="100%" position="relative">
          <VStack spacing={8}>
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Text fontSize="6em" textStyle="jeoparty" color="jeopardy.gold">
                GAME OVER
              </Text>
            </motion.div>

            {hasWinner && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5, type: "spring" }}
              >
                <VStack spacing={4}>
                  <Text fontSize="3em" textStyle="clue" opacity={0.8}>
                    WINNER
                  </Text>
                  <Box
                    bg="jeopardy.gold"
                    px={12}
                    py={6}
                    borderRadius="xl"
                    boxShadow="0 8px 32px rgba(255, 215, 0, 0.4)"
                  >
                    <Text
                      fontSize="5em"
                      fontWeight="bold"
                      color="black"
                      textAlign="center"
                    >
                      {winner.name}
                    </Text>
                  </Box>
                  <Text fontSize="3em" textStyle="clue" color="jeopardy.gold">
                    ${winner.score.toLocaleString()}
                  </Text>
                </VStack>
              </motion.div>
            )}

            {!hasWinner && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Text fontSize="3em" textStyle="clue" opacity={0.8}>
                  No winner
                </Text>
              </motion.div>
            )}

            {/* Show runner-ups */}
            {sortedPlayers.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
              >
                <HStack spacing={8} mt={4}>
                  {sortedPlayers.slice(1, 4).map((player, index) => (
                    <VStack key={player.name} spacing={1}>
                      <Text fontSize="2em" textStyle="clue" opacity={0.6}>
                        {index === 0 ? "2nd" : index === 1 ? "3rd" : "4th"}
                      </Text>
                      <Text fontSize="1.5em" textStyle="clue">
                        {player.name}
                      </Text>
                      <Text fontSize="1.2em" textStyle="clue" opacity={0.7}>
                        ${player.score.toLocaleString()}
                      </Text>
                    </VStack>
                  ))}
                </HStack>
              </motion.div>
            )}
          </VStack>
        </Center>
      </Box>
    );
  }

  // Waiting for players screen
  if (gameState.currentState === JeopardyStates.WaitingForPlayers) {
    const displayPlayers = gameState.players || [];

    return (
      <Box
        width="100vw"
        height="100vh"
        overflow="hidden"
        backgroundColor="jeopardy.blue"
        position="relative"
        style={{
          transform: `scale(${viewportScale})`,
          transformOrigin: "top left",
          width: `${100 / viewportScale}vw`,
          height: `${100 / viewportScale}vh`,
        }}
      >
        <Center width="100%" height="100%" position="relative">
          {/* Player carousel */}
          <PlayerCarousel
            players={displayPlayers}
            playerAudioProgress={gameState.playerAudioProgress}
          />

          <Flex width="100%" height="100%" justify="center" align="center">
            {/* Left side - Game info */}
            <VStack flex={1} spacing={8} p={10}>
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Text fontSize="8em" textStyle="jeoparty">
                  JEOPARTY
                </Text>
              </motion.div>

              

              
            </VStack>

            {/* Right side - QR Code */}
            <Box
              flex={1}
              position="relative"
              display="flex"
              justifyContent="center"
              alignItems="center"
              p="2vh"
              minHeight="60vh"
            >
              {/* QR Code in center */}
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                style={{ position: "relative", zIndex: 10 }}
              >
                <Box
                  bg="white"
                  p="1vh"
                  borderRadius="xl"
                  boxShadow="0 4px 20px rgba(0,0,0,0.3)"
                >
                  <Image
                    src={qrCodeUrl}
                    alt="QR Code"
                    width="25vh"
                    height="25vh"
                  />
                </Box>
              </motion.div>

            </Box>
          </Flex>
        </Center>
      </Box>
    );
  }

  // Game board
  return (
    <Box
      width="100vw"
      height="100vh"
      overflow="hidden"
      backgroundColor="jeopardy.blue"
      position="relative"
      padding="20px"
      boxSizing="border-box"
      style={{
        transform: `scale(${viewportScale})`,
        transformOrigin: "top left",
        width: `${100 / viewportScale}vw`,
        height: `${100 / viewportScale}vh`,
      }}
    >
      <Flex width="100%" height="100%" backgroundColor="jeopardy.blue">
        {/* Left sidebar - Players */}
        <Flex
          py="50px"
          px="30px"
          height="100%"
          width="400px"
          minWidth="400px"
          justifyContent="space-between"
          direction="column"
          flexShrink={0}
        >
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={4}
          >
            {players?.length ? (
              players
                ?.sort((a, b) => b.score - a.score)
                .map((player) => <PlayerBox key={player.name} player={player} />)
            ) : (
              <Center>
                <Text textStyle="clue">No players</Text>
              </Center>
            )}
          </Flex>
          <Box
            position="absolute"
            bottom="10px"
            left="10px"
          >
            <Image src={qrCodeUrl} alt="QR Code" width="60px" height="60px" />
          </Box>
        </Flex>

        {/* Main game board */}
        <Flex
          flex={1}
          height="100%"
          gap={0}
          backgroundColor="black"
          position="relative"
        >
          {finalJeopardy ? (
            <Center
              backgroundColor="jeopardy.clueBlue"
              height="100%"
              width="100%"
              padding="50px"
              flexDir="column"
              justifyContent="center"
            >
              <Text
                as={Center}
                fontSize="6em"
                textStyle="jeoparty"
                textAlign="center"
              >
                FINAL JEOPARTY
              </Text>
              <TextFit
                flexGrow={1}
                as={Center}
                textStyle="clue"
                textAlign="center"
              >
                {gameState?.currentState ===
                  JeopardyStates.CollectFinalJeopardyWagers
                  ? finalJeopardyCategory?.name
                  : gameState?.currentState ===
                    JeopardyStates.MakeFinalJeopardyGuesses
                    ? finalJeopardyClue?.question
                    : finalJeopardyClue?.answer}
              </TextFit>
            </Center>
          ) : (
            (categories
              ?.filter(
                (category) => category.roundType === gameState?.currentRound
              ) || [])
              .map((category, i) => (
                <Flex
                  flex={1}
                  height="100%"
                  width="0"
                  minWidth={0}
                  key={category.id}
                  direction="column"
                  flexShrink={0}
                  gap={0}
                >
                  <BoardBox borderBottomWidth="1px">
                    <AnimatePresence>
                      <motion.div
                        style={{ height: "100%", width: "100%" }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2, delay: 4 }}
                      >
                        <TextFit
                          max={15}
                          min={0.5}
                          as={Center}
                          padding="0"
                          textAlign="center"
                          textStyle="category"
                        >
                          {category.name}
                        </TextFit>
                      </motion.div>
                    </AnimatePresence>
                  </BoardBox>
                  {category.clues.map((clue, j) => {
                    return (
                      <ClueBox
                        renderDelay={clueRenderOrder[i * 5 + j]}
                        key={clue.id}
                        clue={clue}
                      />
                    );
                  })}
                </Flex>
              ))
          )}
        </Flex>
      </Flex>

      {/* Audio unlock prompt overlay */}
      {showAudioPrompt && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.800"
          zIndex={9999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={handleEnableSound}
        >
          <VStack spacing={6}>
            <Text fontSize="4em" textStyle="jeoparty">
              🔊
            </Text>
            <Text textStyle="clue" fontSize="3xl" textAlign="center">
              Tap anywhere to enable sound
            </Text>
          </VStack>
        </Box>
      )}
    </Box>
  );
};
